import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { resendMfaCode } from "../services/userService.js";

export default function MfaPage() {
  const { verifyMfa } = useAuth();
  const navigate      = useNavigate();
  const location      = useLocation();
  const maskedEmail   = location.state?.maskedEmail || "votre email";
  const rememberMe    = location.state?.rememberMe  || false;
  const method        = location.state?.method      || "EMAIL"; // "TOTP" ou "EMAIL"
  const isTotp        = method === "TOTP";

  const [digits,        setDigits]        = useState(["", "", "", "", "", ""]);
  const [error,         setError]         = useState("");
  const [loading,       setLoading]       = useState(false);
  const [resendCooldown,setResendCooldown]= useState(0);
  const [resending,     setResending]     = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => { startCooldown(); }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const startCooldown = () => setResendCooldown(60);

  /**
   * Répartit une chaîne de chiffres (collée, ou reçue d'un coup) sur les
   * cases à partir de startIndex. Ajouté le 15/09/2026 — voir handleChange
   * ci-dessous pour le contexte du bug corrigé.
   */
  const distributeDigits = (str, startIndex = 0) => {
    const chars = str.replace(/\D/g, "").slice(0, 6 - startIndex).split("");
    if (chars.length === 0) return;
    const newDigits = [...digits];
    chars.forEach((c, i) => { newDigits[startIndex + i] = c; });
    setDigits(newDigits);
    setError("");
    inputRefs.current[Math.min(startIndex + chars.length - 1, 5)]?.focus();
    const full = newDigits.join("");
    if (full.length === 6 && newDigits.every(d => d !== "")) submitCode(full);
  };

  /**
   * Modifié le 15/09/2026 — sur certains claviers mobiles (Gboard, clavier
   * Samsung), coller un code livrait directement plusieurs caractères ici
   * via onChange plutôt que via un événement "paste" exploitable, et
   * l'attribut maxLength de la case tronquait alors silencieusement le
   * collage à 1 seul caractère avant même que handlePaste ne s'exécute —
   * d'où le bug "seul le premier caractère est collé". maxLength a été
   * retiré des inputs (voir plus bas) et on gère ici tout ce qui arrive,
   * un chiffre comme plusieurs à la fois.
   */
  const handleChange = (index, value) => {
    const digitsOnly = value.replace(/\D/g, "");

    if (digitsOnly.length > 1) {
      distributeDigits(digitsOnly, index);
      return;
    }

    const newDigits = [...digits];
    newDigits[index] = digitsOnly;
    setDigits(newDigits);
    setError("");
    if (digitsOnly && index < 5) inputRefs.current[index + 1]?.focus();
    if (digitsOnly && index === 5 && newDigits.join("").length === 6) submitCode(newDigits.join(""));
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (digits[index]) { const d = [...digits]; d[index] = ""; setDigits(d); }
      else if (index > 0) inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft"  && index > 0) inputRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) inputRefs.current[index + 1]?.focus();
  };

  // Collage desktop (Ctrl+V) — chemin normal, toujours intercepté proprement.
  // Le filet de sécurité mobile est dans handleChange ci-dessus.
  const handlePaste = (e) => {
    e.preventDefault();
    distributeDigits(e.clipboardData.getData("text"), 0);
  };

  const submitCode = async (code) => {
    setError("");
    setLoading(true);
    try {
      await verifyMfa(code, rememberMe);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Code incorrect ou expiré");
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < 6) { setError("Veuillez saisir les 6 chiffres"); return; }
    submitCode(code);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setError("");
    try {
      await resendMfaCode();
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      startCooldown();
    } catch (err) {
      setError(err.message || "Impossible de renvoyer le code");
    } finally {
      setResending(false);
    }
  };

  const codeComplete = digits.every(d => d !== "");

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"var(--navy-mid)", padding:24 }}>
      <div style={{ width:"100%", maxWidth:420 }}>

        <div style={{ textAlign:"center", marginBottom:32 }}>
          <img src="/logo-cpuc.png" alt="CPUc" style={{ height:48, objectFit:"contain" }} />
        </div>

        <div style={{ background:"var(--card-bg)", borderRadius:16, padding:"32px 28px", boxShadow:"0 4px 24px rgba(0,0,0,.08)" }}>

          <div style={{ width:56, height:56, borderRadius:"50%", background:"rgba(230,51,41,.08)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
            <i className={`bi ${isTotp ? "bi-phone-fill" : "bi-envelope-check"}`} style={{ fontSize:24, color:"#E63329" }} />
          </div>

          <h2 style={{ fontFamily:"Syne,sans-serif", fontSize:22, fontWeight:800, color:"var(--text-1)", textAlign:"center", marginBottom:8 }}>
            Vérification en deux étapes
          </h2>

          {isTotp ? (
            <p style={{ fontSize:13, color:"var(--text-2)", textAlign:"center", marginBottom:28, lineHeight:1.6 }}>
              Ouvrez votre application d'authentification<br />
              et saisissez le code à 6 chiffres affiché.
            </p>
          ) : (
            <p style={{ fontSize:13, color:"var(--text-2)", textAlign:"center", marginBottom:28, lineHeight:1.6 }}>
              Un code à 6 chiffres a été envoyé à<br />
              <strong style={{ color:"var(--text-1)" }}>{maskedEmail}</strong>
            </p>
          )}

          {error && (
            <div style={{ background:"rgba(220,38,38,.08)", border:"1px solid rgba(220,38,38,.2)", borderRadius:10, padding:"10px 14px", marginBottom:20, fontSize:13, color:"#DC2626", display:"flex", alignItems:"center", gap:8 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display:"flex", gap:10, justifyContent:"center", marginBottom:28 }}>
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  value={digit}
                  onChange={e  => handleChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onFocus={e => e.target.select()}
                  disabled={loading}
                  style={{
                    width:46, height:54, textAlign:"center",
                    fontSize:22, fontWeight:700, fontFamily:"Syne,sans-serif",
                    border:`2px solid ${digit ? "#E63329" : error ? "#DC2626" : "var(--border)"}`,
                    borderRadius:10, outline:"none",
                    background: digit ? "rgba(230,51,41,.04)" : "var(--navy-mid)",
                    color:"var(--text-1)", transition:"border-color .15s, background .15s",
                    cursor: loading ? "not-allowed" : "text",
                  }}
                />
              ))}
            </div>

            <button type="submit" disabled={!codeComplete || loading}
              style={{
                width:"100%", padding:"12px 16px", border:"none", borderRadius:10,
                fontSize:14, fontWeight:600, cursor: codeComplete && !loading ? "pointer" : "not-allowed",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                transition:"background .2s",
                background: codeComplete && !loading ? "#E63329" : "var(--navy-soft)",
                color:      codeComplete && !loading ? "#fff"     : "var(--text-3)",
              }}>
              {loading
                ? <><i className="bi bi-arrow-repeat spin" /> Vérification…</>
                : <><i className="bi bi-shield-check" /> Vérifier le code</>
              }
            </button>
          </form>

          {isTotp ? (
            <div style={{ textAlign:"center", marginTop:20, fontSize:12, color:"var(--text-3)" }}>
              <i className="bi bi-info-circle me-1" />
              Le code se renouvelle automatiquement toutes les 30 secondes dans votre application.
            </div>
          ) : (
            <div style={{ textAlign:"center", marginTop:20 }}>
              <span style={{ fontSize:13, color:"var(--text-2)" }}>Vous n'avez pas reçu le code ?{" "}</span>
              <button onClick={handleResend} disabled={resendCooldown > 0 || resending}
                style={{ background:"none", border:"none", fontSize:13, fontWeight:600, padding:0, cursor: resendCooldown > 0 ? "not-allowed" : "pointer", color: resendCooldown > 0 ? "var(--text-3)" : "#E63329" }}>
                {resending ? "Envoi…" : resendCooldown > 0 ? `Renvoyer (${resendCooldown}s)` : "Renvoyer le code"}
              </button>
            </div>
          )}

          <div style={{ textAlign:"center", marginTop:16 }}>
            <button onClick={() => navigate("/login")}
              style={{ background:"none", border:"none", fontSize:12, color:"var(--text-3)", cursor:"pointer", display:"flex", alignItems:"center", gap:4, margin:"0 auto" }}>
              <i className="bi bi-arrow-left" style={{ fontSize:11 }} />
              Retour à la connexion
            </button>
          </div>

        </div>

        <p style={{ textAlign:"center", fontSize:11, color:"var(--text-3)", marginTop:20 }}>
          © {new Date().getFullYear()} CPUc Solutions Technologiques
        </p>
      </div>
    </div>
  );
}